import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft, FaCar, FaEdit, FaUser } from "react-icons/fa";
import { FaCircleCheck } from "react-icons/fa6";
import { MdCancel, MdOutlineMiscellaneousServices } from "react-icons/md";
import Handler from "../../../Helpers/Handler";
import { formatDateTime } from "../../../Helpers/Utils";
import { DetailCard, FieldRow, Skeleton, Badge } from "../../../Components/Needed";
import { serviceStatusVariant } from "../shared/vehicleFleetConstants";
import { useAuth } from "../../../Contexts/AuthenticateProvider";
import Timeline from "../../../Components/Timeline";
import ServiceUpdateModal from "./ServiceUpdateModal";
import ServiceStartModal from "./ServiceStartModal";
import ServiceEndModal from "./ServiceEndModal";
import { listBackPath } from "../../../Helpers/useListParams";

const ServiceDetail = () => {
    const { serviceId } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useAuth();
    const [service, setService] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const [serviceResponse, logResponse] = await Promise.all([
            Handler({ method: "GET", url: `/staff/service/${serviceId}` }),
            Handler({ method: "GET", url: `/staff/service/${serviceId}/log`, params: { limit: 20 } }),
        ]);
        setService(serviceResponse?.success ? serviceResponse?.data?.data : null);
        setLogs(logResponse?.success ? logResponse?.data?.data || [] : []);
        setLoading(false);
    }, [serviceId]);

    useEffect(() => { load(); }, [load]);

    const closeModal = () => setModal(null);
    const onSaved = () => { closeModal(); load(); };

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="30%" className="mb-3" />
                <Skeleton height={120} />
            </div>
        );
    }

    if (!service) {
        return (
            <div className="settings-card text-center py-5">
                <p className="text-muted mb-3">Service record not found.</p>
                <button type="button" className="btn back-btn" onClick={() => navigate(listBackPath("/vehicle-fleet/services"))}>Back to services</button>
            </div>
        );
    }

    const vehiclePath = service.vehicleType && service.vehicleId ? `/vehicle-fleet/${service.vehicleType}/${service.vehicleId}` : null;
    const canUpdate = hasPermission("service.update");
    const canStart = hasPermission("service.start");
    const canEnd = hasPermission("service.end");

    return (
        <>
            <button type="button" className="btn back-btn text-decoration-none px-3 mb-3 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/vehicle-fleet/services"))}>
                <FaArrowLeft /> Back to services
            </button>

            <div className="row g-4">
                <div className="col-12 col-lg-7">
                    <DetailCard>
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                            <div>
                                <h3 className="fw-bold mb-1 d-flex align-items-center gap-2"><MdOutlineMiscellaneousServices /> {service.serviceName}</h3>
                                <small className="text-muted d-block mb-2">#{service.serviceId}</small>
                                <div className="d-flex flex-wrap gap-2">
                                    <Badge
                                        text={service.status ? "Active" : "Inactive"}
                                        icon={service.status ? <FaCircleCheck /> : <MdCancel />}
                                        className={service.status ? "badge-active" : "badge-inactive"}
                                    />
                                    <span className={`badge d-flex align-items-center justify-cotent-center rounded-pill ${serviceStatusVariant(service.serviceStatus)}`}>{service.serviceStatus}</span>
                                    <span className="badge d-flex align-items-center justify-cotent-center rounded-pill text-bg-light border text-capitalize">{service.serviceType}</span>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                {service.serviceStatus === "Initiated" && canUpdate ? (
                                    <button type="button" className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={() => setModal("update")}><FaEdit /> Edit</button>
                                ) : null}
                                {service.serviceStatus === "Initiated" && canStart ? (
                                    <button type="button" className="btn btn-sm btn-primary" onClick={() => setModal("start")}>Start</button>
                                ) : null}
                                {service.serviceStatus === "Started" && canEnd ? (
                                    <button type="button" className="btn btn-sm btn-primary" onClick={() => setModal("end")}>End</button>
                                ) : null}
                            </div>
                        </div>

                        <div className="row g-3">
                            <div className="col-12 col-md-6 d-flex align-items-center gap-2">
                                <FaCar className="text-muted flex-shrink-0" />
                                {service.vehicle ? (
                                    vehiclePath ? (
                                        <button type="button" className="btn btn-link p-0 text-capitalize" onClick={() => navigate(vehiclePath)}>
                                            {service.vehicle.registrationNo}
                                        </button>
                                    ) : (
                                        <span className="small text-muted">{service.vehicle.registrationNo}</span>
                                    )
                                ) : <span className="text-muted small">-</span>}
                            </div>
                            {/* <div className="col-12 col-md-6 d-flex align-items-center gap-2">
                                <FaUser className="text-muted flex-shrink-0" />
                                <span className="text-capitalize">{service.staffName || "-"}</span>
                            </div> */}
                        </div>

                        {/* <FieldRow label="Created" value={formatDateTime(service.createdAt)} /> */}

                        {service.serviceStatus === "Started" || service.serviceStatus === "Closed" ? (
                            <div className="row g-3 mt-4 pt-3 border-top">
                                <div className="col-12">
                                    <FieldRow
                                        label="Service center"
                                        value={(
                                            <span className="d-inline-flex align-items-center gap-2">
                                                {service.serviceCenter || "-"}
                                                {service.serviceStatus === "Started" && canStart ? (
                                                    <button type="button" className="btn p-0 d-inline-flex" onClick={() => setModal("start-update")}><FaEdit /></button>
                                                ) : null}
                                            </span>
                                        )}
                                    />
                                    <FieldRow label="Service center type" value={service.serviceCenterType || "-"} />
                                    <FieldRow label="Started at" value={formatDateTime(service.servicedStartAt)} />
                                    {service.serviceStatus === "Closed" ? (
                                        <>
                                            <FieldRow label="Ended at" value={formatDateTime(service.servicedEndAt)} />
                                            <FieldRow label="Odometer reading" value={service.vehicleKm !== undefined ? `${service.vehicleKm} km` : "-"} />
                                            <FieldRow label="Total cost" value={service.totalCost !== undefined ? `₹${service.totalCost}` : "-"} />
                                            <FieldRow label="Payment" value={`${service.paymentMode || "-"} - ${service.paymentBy || "-"}`} />
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}
                    </DetailCard>

                    {service.serviceDetails?.length ? (
                        <DetailCard title="Service Details">
                            <div className="overflow w-100">
                                <table className="table table-bordered rounded-1 align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th>S.No</th>
                                            <th>Part Name</th>
                                            <th>Service Action</th>
                                            <th>Cost</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {service.serviceDetails.map((d, i) => (
                                            <tr key={i}>
                                                <td>{i + 1}</td>
                                                <td className="text-capitalize">{d.partName || "-"}</td>
                                                <td>{d.serviceAction || "-"}</td>
                                                <td>₹{d.cost ?? "-"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </DetailCard>
                    ) : null}
                </div>

                <div className="col-12 col-lg-5">
                    <Timeline userLog={logs} />
                </div>
            </div>

            {modal === "update" ? <ServiceUpdateModal service={service} onClose={closeModal} onSaved={onSaved} /> : null}
            {modal === "start" ? <ServiceStartModal service={service} mode="start" onClose={closeModal} onSaved={onSaved} /> : null}
            {modal === "start-update" ? <ServiceStartModal service={service} mode="update" onClose={closeModal} onSaved={onSaved} /> : null}
            {modal === "end" ? <ServiceEndModal service={service} onClose={closeModal} onSaved={onSaved} /> : null}
        </>
    );
};

export default ServiceDetail;
